import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router';
import { ApiError } from '../../lib/api';
import {
  operatorConsole,
  type PackBuilderCreateAttrs,
  type PackBuilderLimits,
  type PackBuilderPoolCategory,
} from '../../lib/api/operatorConsole';
import { useMe } from '../../auth/MeContext';
import { Button } from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import { FormField } from '../../components/ui/FormField';
import { Input } from '../../components/ui/Input';
import { RangeInput } from '../../components/ui/RangeInput';
import { Select } from '../../components/ui/Select';
import { formatPercent } from './packBuilderFormat';

const DEFAULT_RTP = 0.85;
const MIN_RTP = 0.05;
const RTP_STEP = 0.01;
const PRICE_ERROR = 'Enter a positive amount with at most two decimals.';

/** '' is "not chosen yet", NOT "any": the mix has to be picked deliberately,
 *  or the first option silently becomes the answer for most packs. Only
 *  'graded'/'ungraded' travel to the backend; 'any' is the absent column. */
type GradeType = '' | 'any' | 'graded' | 'ungraded';

const GRADE_TYPE_OPTIONS: { value: GradeType; label: string }[] = [
  { value: '', label: 'Choose…' },
  { value: 'any', label: 'Any (graded and raw)' },
  { value: 'graded', label: 'Graded only' },
  { value: 'ungraded', label: 'Raw only' },
];

function asGradeType(value: string): GradeType {
  return value === 'any' || value === 'graded' || value === 'ungraded' ? value : '';
}

/** Backend keeps price at scale 2 — anything finer is rejected here first. */
function parsePrice(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return value > 0 ? value : null;
}

export function NewPackPage() {
  const { me } = useMe();
  const navigate = useNavigate();
  const currency = me?.operator.currency ?? null;

  const [limits, setLimits] = useState<PackBuilderLimits | null>(null);
  const [categories, setCategories] = useState<PackBuilderPoolCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hidden, setHidden] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [rtp, setRtp] = useState(DEFAULT_RTP);
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [gradeType, setGradeType] = useState<GradeType>('');
  const [badge, setBadge] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        // The RTP ceiling only comes with the pack list. Categories come from
        // pack_builder/pool_categories — every pool category, not only those
        // that already have an enabled pack (CONTRACT §4.12).
        const [list, cats] = await Promise.all([
          operatorConsole.packBuilder.listPacks(),
          operatorConsole.packBuilder.listPoolCategories(),
        ]);
        if (cancelled) return;
        setLimits(list.limits);
        setCategories(cats.pool_categories);
        setRtp((current) => Math.min(current, list.limits.max_rtp));
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setHidden(true);
          return;
        }
        setLoadError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const parsedPrice = parsePrice(price);
  const priceInvalid = price.trim() !== '' && parsedPrice === null;
  // PoolOffer validates title AND description presence — an empty description
  // would only come back as a 422, so the form gates it here.
  const canSubmit =
    !submitting &&
    title.trim() !== '' &&
    description.trim() !== '' &&
    parsedPrice !== null &&
    gradeType !== '' &&
    limits !== null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit || parsedPrice === null) return;
    const attrs: PackBuilderCreateAttrs = {
      title: title.trim(),
      description: description.trim(),
      price: parsedPrice,
      stated_rtp: rtp,
      pool_category_ids: categoryIds,
      ...(gradeType === 'graded' || gradeType === 'ungraded'
        ? { card_grade_type: gradeType }
        : {}),
      ...(badge.trim() ? { badge_name: badge.trim() } : {}),
    };
    try {
      setSubmitting(true);
      setSubmitError(null);
      const pack = await operatorConsole.packBuilder.createPack(attrs);
      navigate(`/my-packs/${pack.id}?tab=items`);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Could not create the pack');
    } finally {
      setSubmitting(false);
    }
  }

  const toggleCategory = (id: number, checked: boolean) => {
    setCategoryIds((prev) => (checked ? [...prev, id] : prev.filter((c) => c !== id)));
  };

  if (hidden) return <Navigate to="/" replace />;
  if (loading) return <div className="p-6 text-[13px] text-[var(--co-muted)]">Loading…</div>;
  if (loadError || !limits) {
    return (
      <div className="p-6 text-[13px] text-[var(--co-error)]">Error: {loadError ?? 'Unknown error'}</div>
    );
  }

  return (
    <div className="max-w-2xl space-y-4 p-6">
      <div>
        <Link to="/my-packs" className="text-[12px] text-[var(--co-muted)] hover:underline">
          ← My Packs
        </Link>
        <h1 className="text-2xl font-bold">New pack</h1>
        <p className="text-[12px] text-[var(--co-muted)]">
          Price and RTP set the odds ladder; both can be changed until the pack is published.
        </p>
      </div>

      <form
        aria-label="New pack"
        onSubmit={(e) => void handleSubmit(e)}
        className="space-y-4 rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] p-4"
      >
        <FormField label="Title" htmlFor="pack-title">
          <Input
            id="pack-title"
            aria-label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={255}
            placeholder="Rookie Hunt"
          />
        </FormField>

        <FormField label="Description" htmlFor="pack-description">
          <textarea
            id="pack-description"
            aria-label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What is inside this pack (required)"
            rows={3}
            maxLength={2000}
            className="w-full rounded-md border border-[var(--co-border)] bg-[var(--co-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--co-brand)]"
          />
        </FormField>

        <FormField label="Price" htmlFor="pack-price" error={priceInvalid ? PRICE_ERROR : undefined}>
          <div className="flex items-center gap-2">
            <Input
              id="pack-price"
              aria-label="Price"
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              aria-invalid={priceInvalid}
              className="w-40"
            />
            {currency && <span className="text-[12px] text-[var(--co-muted)]">{currency}</span>}
          </div>
        </FormField>

        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-[var(--co-text)]">RTP</span>
          <RangeInput
            aria-label="RTP"
            value={rtp}
            min={MIN_RTP}
            max={limits.max_rtp}
            step={RTP_STEP}
            onChange={setRtp}
            format={(v) => formatPercent(v)}
          />
          <p className="text-[11px] text-[var(--co-muted)]">
            Set the average return to player as a percentage of pack price. Range: 85–
            {formatPercent(limits.max_rtp)}.
            {rtp < limits.min_rtp_warn
              ? ` Below ${formatPercent(limits.min_rtp_warn)} the validator warns about a greedy pack.`
              : ''}
          </p>
        </div>

        <fieldset className="border-0 p-0">
          <legend className="text-sm font-medium text-[var(--co-text)]">Categories</legend>
          <p className="text-[11px] text-[var(--co-muted)]">
            Select one or more categories. Packs can appear in multiple sections.
          </p>
          {categories.length === 0 ? (
            <p className="text-[11px] text-[var(--co-muted)]">
              No pool categories exist yet. You can still create the pack; add a category before
              publishing.
            </p>
          ) : (
            <div className="mt-1.5 grid gap-2 sm:grid-cols-2">
              {categories.map((c) => (
                <Checkbox
                  key={c.id}
                  aria-label={`Category: ${c.name}`}
                  label={c.name}
                  checked={categoryIds.includes(c.id)}
                  onChange={(checked) => toggleCategory(c.id, checked)}
                />
              ))}
            </div>
          )}
        </fieldset>

        <FormField label="Grade type" htmlFor="pack-grade-type">
          <Select
            id="pack-grade-type"
            aria-label="Grade type"
            value={gradeType}
            options={GRADE_TYPE_OPTIONS}
            onChange={(v) => setGradeType(asGradeType(v))}
          />
          <p className="text-[11px] text-[var(--co-muted)]">
            Set whether this pack contains graded cards, ungraded cards, or a mix of both.
          </p>
        </FormField>

        <FormField label="Pack name" htmlFor="pack-badge">
          <Input
            id="pack-badge"
            aria-label="Pack name"
            value={badge}
            onChange={(e) => setBadge(e.target.value)}
            maxLength={255}
            placeholder="NEW"
          />
        </FormField>

        {submitError && (
          <p role="alert" className="text-[12px] text-[var(--co-error)]">
            {submitError}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => navigate('/my-packs')}>
            Cancel
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            {submitting ? 'Creating…' : 'Create pack'}
          </Button>
        </div>
      </form>
    </div>
  );
}
